import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { RotateCcw, Smartphone, TabletSmartphone } from 'lucide-react';
import { useDeviceDetection, useOrientation } from '../hooks/useDeviceDetection';
import { useIOSPWADetection } from '../hooks/useIOSPWADetection';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/hooks';
import { closeMenu } from '../store/slices/navigationSlice';

interface OrientationBlockerProps {
    /** Whether to enable the orientation blocker */
    enabled?: boolean;
    /** Custom message to display */
    message?: string;
    /** Custom title to display */
    title?: string;
    /** Whether to apply only to mobile devices (excludes tablets) */
    mobileOnly?: boolean;
    /** Whether to apply only to tablet devices (excludes mobile) */
    tabletOnly?: boolean;
    /** Custom background color (defaults to black) */
    backgroundColor?: string;
    /** Custom text color (defaults to white) */
    textColor?: string;
    /** Whether to show the visual device rotation indicator */
    showIndicator?: boolean;
    /** Whether to show debugging info in development */
    showDebugInfo?: boolean;
}

export default function OrientationBlocker({
    enabled = true,
    message,
    title,
    mobileOnly = true,
    tabletOnly = false,
    backgroundColor = 'bg-black',
    textColor = 'text-white',
    showIndicator = true,
    showDebugInfo = true
}: OrientationBlockerProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const deviceInfo = useDeviceDetection();
    const orientation = useOrientation();
    const { isIOSPWA } = useIOSPWADetection();

    // Determine if the blocker should be active
    const shouldShowBlocker = () => {
        if (!enabled) return false;
        
        // Only show on mobile/tablet devices
        if (mobileOnly && !deviceInfo.isMobile) return false;
        if (tabletOnly && !deviceInfo.isTablet) return false;
        
        // If neither mobileOnly nor tabletOnly is specified, apply to both mobile and tablet
        if (!mobileOnly && !tabletOnly) {
            if (!deviceInfo.isMobile && !deviceInfo.isTablet) return false;
        }
        
        // Show blocker when in landscape mode
        return orientation === 'landscape';
    };

    const isBlocking = shouldShowBlocker();

    // Close the side menu when orientation blocker becomes active
    useEffect(() => {
        if (isBlocking) {
            dispatch(closeMenu());
        }
    }, [isBlocking, dispatch]);

    // Lock/unlock body scroll when blocker is active
    useEffect(() => {
        if (isBlocking) {
            // Add class to body to prevent scrolling
            document.body.classList.add('orientation-blocked');
            
            // Also set styles directly as backup
            const originalStyle = {
                overflow: document.body.style.overflow,
                position: document.body.style.position,
                width: document.body.style.width,
                height: document.body.style.height
            };
            
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.height = '100%';
            
            return () => {
                // Remove class and restore original styles
                document.body.classList.remove('orientation-blocked');
                document.body.style.overflow = originalStyle.overflow;
                document.body.style.position = originalStyle.position;
                document.body.style.width = originalStyle.width;
                document.body.style.height = originalStyle.height;
            };
        }
    }, [isBlocking]);

    if (!isBlocking) {
        return null;
    }

    const defaultTitle = t('common.orientationBlockerTitle');
    const defaultMessage = t('common.orientationBlockerMessage');
    
    const displayTitle = title || defaultTitle;
    const displayMessage = message || defaultMessage;

    // Choose appropriate device icon
    const DeviceIcon = deviceInfo.isMobile ? Smartphone : TabletSmartphone;

    // For iOS PWA, render directly to document.body to completely bypass layout containers
    const blockerContent = (
        <div 
            className={`orientation-blocker-overlay ${backgroundColor} flex items-center justify-center p-6 animate-fade-in ${isIOSPWA ? 'ios-pwa-orientation-blocker' : ''}`}
        >
            <div className={`text-center ${textColor} max-w-lg mx-auto px-4 pt-8 animate-slide-up`}>
                {/* Rotation Icon */}
                <div className="flex justify-center mb-8">
                    <div className="relative">
                        <DeviceIcon 
                            size={72} 
                            className="text-white/80 transform rotate-90 transition-transform duration-700" 
                            style={{
                                animation: 'pulse 2s infinite'
                            }}
                        />
                        <RotateCcw 
                            size={36} 
                            className="text-white absolute -top-3 -right-3 animate-spin-slow" 
                        />
                    </div>
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-bold mt-2 mb-4 tracking-tight">
                    {displayTitle}
                </h1>
                
                {/* Message */}
                <p className="text-lg md:text-xl text-white/90 leading-tight mb-0 font-medium">
                    {displayMessage}
                </p>

                {/* Visual indicator */}
                {showIndicator && (
                    <div className="flex justify-center items-center space-x-6 mb-8">
                        <div className="flex flex-col items-center text-white/60">
                            <DeviceIcon size={32} className="transform rotate-90 mb-2" />
                            <span className="text-sm font-medium">Landscape</span>
                        </div>
                        
                        <div className="text-white/40 text-3xl font-light animate-pulse">→</div>
                        
                        <div className="flex flex-col items-center text-white">
                            <DeviceIcon size={32} className="mb-2" />
                            <span className="text-sm font-bold">Portrait</span>
                        </div>
                    </div>
                )}

                {/* Instruction text */}
                <div className="text-white/70 text-sm font-medium">
                    <p 
                        className="animate-bounce"
                        style={{
                            animationDelay: '1s'
                        }}
                    >
                        {t('common.orientationBlockerInstruction', { deviceType: deviceInfo.type })}
                    </p>
                </div>

                {/* Device info for debugging (only in development) */}
                {process.env.NODE_ENV === 'development' && showDebugInfo && (
                    <div className="mt-8 p-4 bg-white/10 rounded-lg text-xs text-white/70 font-mono">
                        <div className="grid grid-cols-2 gap-2 text-left">
                            <div>Device: {deviceInfo.type}</div>
                            <div>Orientation: {orientation}</div>
                            <div>Screen: {deviceInfo.screenWidth}×{deviceInfo.screenHeight}</div>
                            <div>Touch: {deviceInfo.isTouchDevice ? 'Yes' : 'No'}</div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
    
    // For iOS PWA, use portal to render outside of layout container
    if (isIOSPWA) {
        return createPortal(blockerContent, document.body);
    }
    
    // For regular browser mode, render normally
    return blockerContent;
}

/**
 * Simple version that only blocks landscape mode on mobile/tablet devices
 */
export function SimpleLandscapeBlocker({ 
    enabled = true,
    message = "This app works best in portrait mode. Please rotate your device."
}: { 
    enabled?: boolean;
    message?: string;
}) {
    return (
        <OrientationBlocker 
            enabled={enabled}
            message={message}
            showIndicator={true}
            showDebugInfo={false}
        />
    );
}

/**
 * Minimal version with just text
 */
export function MinimalOrientationBlocker({ 
    enabled = true 
}: { 
    enabled?: boolean;
}) {
    return (
        <OrientationBlocker 
            enabled={enabled}
            title="Please Rotate"
            message="Turn your device to portrait mode"
            showIndicator={false}
            showDebugInfo={false}
        />
    );
} 